import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { getAiStatus } from '../../services/system-health.api';

/**
 * Tells an administrator, on every page, when the AI provider has stopped
 * answering.
 *
 * The Anthropic credit ran out on 27 September 2026 and the portal carried on
 * looking healthy for two days: articles kept arriving, pages kept rendering,
 * and the only symptom was a daily brief whose date quietly stopped moving. Log
 * lines existed, in their hundreds, in a container log that rolls off after a
 * day. So the requirement here is not more detail, it is placement: the one
 * thing that must not happen again is that the platform knows and no one is
 * told.
 *
 * Kept in English like the System Health page it links to. This is operational
 * text for the person who administers the platform, not copy that reaches a
 * reader, and the server's summary line is English in any case.
 */
export const AiServiceBanner: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [dismissed, setDismissed] = React.useState(false);

  const { data } = useQuery({
    queryKey: ['aiStatus'],
    queryFn: getAiStatus,
    enabled: isAdmin,
    // Five minutes: an exhausted balance does not refill on its own, and this
    // sits on every page load.
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60_000,
    // A failing status endpoint must never surface as a page error; absence of
    // a banner is the correct fallback.
    retry: false,
  });

  if (!isAdmin || dismissed) return null;
  if (!data || data.degraded.length === 0) return null;

  // Credit and key problems block everything and need an account change; a run
  // of transient failures is worth knowing about but is not the same event.
  const blocking = data.creditExhausted || data.authFailed;
  const tone = blocking
    ? 'border-red-300 bg-red-50 text-red-900'
    : 'border-amber-300 bg-amber-50 text-amber-900';

  return (
    <div
      role="status"
      className={`mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 ${tone}`}
    >
      <AlertTriangle size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          {data.creditExhausted
            ? 'AI features are stopped: Anthropic credit exhausted'
            : data.authFailed
              ? 'AI features are stopped: Anthropic API key rejected'
              : 'AI features are failing'}
        </p>
        <p className="mt-0.5 text-sm">{data.summary}</p>
        <p className="mt-1 text-xs opacity-80">
          Article collection is unaffected and continues to run.{' '}
          <Link to="/system-health" className="underline underline-offset-2">
            Open System Health
          </Link>
          {data.creditExhausted && (
            <>
              {' '}or add credit at{' '}
              <a
                href="https://console.anthropic.com/settings/billing"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                console.anthropic.com
              </a>
              .
            </>
          )}
        </p>
      </div>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss for this session"
        className="shrink-0 rounded p-1 opacity-60 hover:opacity-100"
      >
        <X size={15} />
      </button>
    </div>
  );
};

export default AiServiceBanner;
